/**
 * This file is part of ONP.
 *
 * Copyright (c) Opensoft (http://opensoftdev.com)
 *
 * The unauthorized use of this code outside the boundaries of
 * Opensoft is prohibited.
 *
 * @author Ivetta Tkachenko <ivetta.tkachenko@opensoftdev.ru>
 */

define(
    [
        'jquery',
        'underscore',
        'backbone',
        'assets/js/modules/utils',
        'assets/js/modules/api/product',
        'assets/js/modules/OperationProcessor',
        'assets/js/modules/product/model/productModel',
        'assets/js/modules/product/model/attributeModel',
        'assets/js/modules/product/model/skuModel',
        'assets/js/modules/widgets/pricingCalculator/model/pricingCalcSideModel',
        'assets/js/modules/option/optionModel',
        'assets/js/modules/option/optionValueModel',
        'assets/js/modules/widgets/pricingCalculator/model/pricingCalcItemModel',
        'assets/js/modules/widgets/pricingCalculator/model/pricingCalcSkuModel',
        'assets/js/modules/widgets/pricingCalculator/model/pricingCalcOptionModel',
        'assets/js/modules/widgets/pricingCalculator/model/pricingCalcOptionValueModel',
        'assets/js/modules/pricing/surchargePricingModel',
        'assets/js/modules/widgets/pricingCalculator/pricingCalculatorService'
    ],
    function(
        $,
        _,
        backbone,
        utils,
        productApi,
        OperationProcessor,
        ProductModel,
        AttributeModel,
        SkuModel,
        PricingCalcSideModel,
        OptionModel,
        OptionValueModel,
        PricingCalcItemModel,
        PricingCalcSkuModel,
        PricingCalcOptionModel,
        PricingCalcOptionValueModel,
        SurchargePricingModel,
        pricingCalculatorService
    ) {
        /**
         * @param {PricingCalcModel} model
         */
        var PricingCalculator = function(model) {
            var _this = this;
            _.extend(this, backbone.Events);

            this.model = model;

            this.listenTo(this.model.get('eventAgent'), {
                'PricingCalcItem:changeSku': function(skuId, pricingCalcItem) {
                    _this.changeSku(skuId, pricingCalcItem);
                },
                'PricingCalcItem:changeOption': function(optionValueName, optionName, pricingCalcItem) {
                    _this.changeOption(optionValueName, optionName, pricingCalcItem);
                },
                'PricingCalcItem:changeSide': function(sideCount, pricingCalcItem) {
                    _this.changeSide(sideCount, pricingCalcItem);
                },
                'PricingCalculator:changeShipping': function(shipping) {
                    _this.changeShipping(shipping);
                },
                'PricingCalculator:changeZip': function(zip) {
                    _this.setZip(zip);
                }
            });

            this.listenTo(this.model, 'change:availableShipping', function() {
                var closestAvailableShipping = this.model.getClosestAvailableShipping();
                if (closestAvailableShipping === undefined || closestAvailableShipping === null) {
                    this.model.dropShipping();
                } else {
                    this.model.setShipping(closestAvailableShipping);
                }
                this.model.get('eventAgent').trigger('PricingCalculator:shippingUpdated');
            });
        };

        PricingCalculator.prototype = {
            createItem: function(product, currency, successCallback, errorCallback) {
                var _this = this;

                this.loadSurchargePricings(product.get('options').where({ required: true }))
                    .done(function() {
                        let pricingCalcItem = new PricingCalcItemModel({
                            product: product,
                            currency: currency,
                            vatRate: _this.model.get('vatRate'),
                            skus: _this.createSkus(product.get('skus')),
                            options: _this.createOptions(product.get('options')),
                            sides: pricingCalculatorService.createSides(product.get('skus'))
                        });

                        successCallback(pricingCalcItem);
                    })
                    .fail(function(err, slug) {
                        errorCallback(err, slug);
                    });
            },

            /**
             * @param {OptionModel[]} options
             */
            loadSurchargePricings: function(options) {
                var deferred = $.Deferred();
                var requiredSlugs = this.getRequiredSlugsForPricings(options);
                var processor = new OperationProcessor();

                var pricingProductSkus = {};
                _.each(requiredSlugs, function(slug) {
                    processor.add(
                        productApi.getProduct(slug).once('success', function(data) {
                            var skus = new backbone.Collection();
                            _.each(data.skus, function(skuDTO) {
                                var sku = new SkuModel().fromDTO(skuDTO);
                                if (sku.get('enabled')) {
                                    skus.add(sku);
                                }
                            });
                            pricingProductSkus[slug] = skus;
                        })
                    );
                });

                processor
                    .getOperation()
                    .once('success', function() {
                        options.forEach(function(option) {
                            option.get('optionValues').each(function(optionValue) {
                                var pricing = optionValue.get('pricing');

                                if (pricing instanceof SurchargePricingModel) {
                                    pricing.set('productSkus', pricingProductSkus[pricing.get('productSlug')]);
                                }
                            });
                        });

                        deferred.resolve();
                    })
                    .once('error', function(err, slug) {
                        deferred.reject(err, slug);
                    })
                    .start();

                return deferred.promise();
            },

            /**
             * @param {OptionModel[]} options
             * @returns {string[]}
             */
            getRequiredSlugsForPricings: function(options) {
                var slugs = [];
                options.forEach(function(option) {
                    option.get('optionValues').each(function(optionValue) {
                        var pricing = optionValue.get('pricing');

                        if (pricing instanceof SurchargePricingModel) {
                            slugs.push(pricing.get('productSlug'));
                        }
                    });
                });

                return slugs;
            },

            /**
             * @param {SkuModel[]} skus
             * @returns {PricingCalcSkuModel[]}
             */
            createSkus: function(skus) {
                var _this = this;
                var pricingCalcSkus = new backbone.Collection();

                skus.forEach(function(sku) {
                    if (sku.get('enabled')) {
                        pricingCalcSkus.add(
                            new PricingCalcSkuModel({
                                sku: sku,
                                vatRate: _this.model.get('vatRate'),
                                price: sku.getPrice(),
                                salePrice: sku.getPrice(_this.model.get('pricingSetName'))
                            })
                        );
                    }
                });

                return pricingCalcSkus;
            },

            /**
             * @param {OptionModel[]} options
             * @returns {PricingCalcOptionModel[]}
             */
            createOptions: function(options) {
                const _this = this;
                var pricingCalcOptions = new backbone.Collection();

                options.each(function(option) {
                    var pricingCalcOptionValues = new backbone.Collection();

                    option.get('optionValues').each(function(optionValue) {
                        if (optionValue.get('enabled')) {
                            var pricingCalcOptionValue = new PricingCalcOptionValueModel({
                                optionValue: optionValue,
                                visible: optionValue.get('visible'),
                                vatRate: _this.model.get('vatRate')
                            });
                            pricingCalcOptionValues.add(pricingCalcOptionValue);
                        }
                    });

                    if (pricingCalcOptionValues.length > 0) {
                        var pricingCalcOption = new PricingCalcOptionModel({
                            option: option,
                            price: option.get('price'),
                            vatRate: _this.model.get('vatRate'),
                            visible: option.get('visible'),
                            optionValues: pricingCalcOptionValues
                        });
                        pricingCalcOptions.add(pricingCalcOption);
                    }
                });

                return pricingCalcOptions;
            },

            /**
             * @param {PricingCalcItemModel} pricingCalcItem
             * @param {number|null} skuIdToSelect
             * @param {Object} optionsToSelect
             */
            selectSkuAndOptionsInitially: function(pricingCalcItem, skuIdToSelect, optionsToSelect) {
                this.selectSkuAndOptions(pricingCalcItem, optionsToSelect, skuIdToSelect);

                this.updateItemPrices(pricingCalcItem);
            },

            /**
             * @param {int} skuId
             * @param {PricingCalcItemModel} pricingCalcItem
             */
            changeSku: function(skuId, pricingCalcItem) {
                var oldOptionsSet = _.clone(pricingCalcItem.get('optionSet'));
                var oldQuantity = pricingCalcItem.get('quantity');
                var oldSideCount = pricingCalcItem.get('sideCount');

                this.selectSkuAndOptions(pricingCalcItem, {}, skuId, oldOptionsSet, oldQuantity, oldSideCount);

                this.updateItemPrices(pricingCalcItem);
                this.updateTotalPrice();

                var isOptionSetChanged = !_.isEqual(oldOptionsSet, pricingCalcItem.get('optionSet'));
                var isSideCountChanged = oldSideCount !== pricingCalcItem.get('sideCount');

                pricingCalcItem.trigger('changed', true, isOptionSetChanged, isSideCountChanged);
                this.model
                    .get('eventAgent')
                    .trigger('PricingCalculator:changed', pricingCalcItem, true, isOptionSetChanged, isSideCountChanged);
            },

            /**
             * @param {number} sideCount
             * @param {PricingCalcItemModel} pricingCalcItem
             */
            changeSide: function(sideCount, pricingCalcItem) {
                var quantity = pricingCalcItem
                    .getSelectedSku()
                    .get('sku')
                    .get('quantity');
                var newSku = pricingCalcItem.findSkuBy({ sides: sideCount, quantity: quantity });
                if (!newSku) {
                    newSku = pricingCalcItem.findSkuBy({ sides: sideCount });
                }
                this.changeSku(newSku.get('sku').get('id'), pricingCalcItem);
            },

            /**
             * @param {string} optionValueName
             * @param {string} optionName
             * @param {PricingCalcItemModel} pricingCalcItem
             */
            changeOption: function(optionValueName, optionName, pricingCalcItem) {
                var optionNames = {};
                optionNames[optionName] = optionValueName;

                var oldOptionsSet = _.clone(pricingCalcItem.get('optionSet'));
                var oldQuantity = pricingCalcItem.get('quantity');
                var oldSideCount = pricingCalcItem.get('sideCount');

                this.selectSkuAndOptions(pricingCalcItem, optionNames, null, oldOptionsSet, oldQuantity, oldSideCount);

                this.updateItemPrices(pricingCalcItem);
                this.updateTotalPrice();

                var isSideCountChanged = oldSideCount !== pricingCalcItem.get('sideCount');
                var isSkuChanged = oldQuantity !== pricingCalcItem.get('quantity') || isSideCountChanged;

                pricingCalcItem.trigger('changed', isSkuChanged, true, isSideCountChanged);
                this.model.get('eventAgent').trigger('PricingCalculator:changed', pricingCalcItem, isSkuChanged, true, isSideCountChanged);
            },

            /**
             * Selects sku and options considering conflicts and dependencies
             * Reduced number of cycles to a minimum for best productivity
             *
             * @param {PricingCalcItemModel} pricingCalcItem
             * @param {Object} optionNamesToSelect
             * @param {number} skuIdToSelect
             * @param {Object} previousOptions
             * @param {number} previousQuantity
             * @param {number} previousSideCount
             */
            selectSkuAndOptions: function(
                pricingCalcItem,
                optionNamesToSelect,
                skuIdToSelect,
                previousOptions,
                previousQuantity,
                previousSideCount
            ) {
                var optionName;
                var optionValueName;
                previousOptions = previousOptions || {};
                var previousSku;
                var selectedOptions = {};
                var selectedSku;
                var optionDependencies = [];
                var optionConflicts = [];
                var skuConflicts = [];
                var resolvedDependencies = [];
                var resolvedConflicts = [];

                pricingCalcItem.get('skus').each(function(pricingCalcSku) {
                    var sku = pricingCalcSku.get('sku');
                    // reset sku
                    pricingCalcSku.set('selected', false);
                    pricingCalcSku.set('enabled', true);
                    //get previously selected sku
                    if (sku.get('quantity') === previousQuantity && sku.get('sides') === previousSideCount) {
                        previousSku = pricingCalcSku;
                    }
                    if (sku.get('id') === skuIdToSelect) {
                        selectedSku = pricingCalcSku;
                        selectedSku.set('selected', true);
                        // only gather conflicts if sku MUST be selected
                        optionConflicts = sku.get('optionValueConflicts');
                    }
                });

                pricingCalcItem.get('options').each(function(pricingCalcOption) {
                    optionName = pricingCalcOption.get('option').get('name');
                    pricingCalcOption.get('optionValues').each(function(pricingCalcOptionValue) {
                        optionValueName = pricingCalcOptionValue.get('optionValue').get('name');

                        // reset option value
                        pricingCalcOptionValue.set('selected', false);
                        pricingCalcOptionValue.set('enabled', true);

                        // select options if they MUST to be selected
                        if (optionValueName === optionNamesToSelect[optionName]) {
                            selectOptionValue(pricingCalcOptionValue, optionName);
                        }
                    });
                    // reset option
                    pricingCalcOption.set('enabled', true);
                });

                resolveConflictsAndDependencies();

                resolveOptions();

                skuConflicts.forEach(function(skuId) {
                    var pricingCalcSku = pricingCalcItem.findSkuBy({ id: skuId });
                    if (pricingCalcSku) {
                        pricingCalcSku.set('selected', false);
                        pricingCalcSku.set('enabled', false);
                    }
                });

                var addressesNumber = pricingCalcItem.get('mailingListRecords');
                if (addressesNumber) {
                    pricingCalcItem.get('skus').each(function(pricingCalcSku) {
                        if (pricingCalcSku.get('sku').get('quantity') < addressesNumber) {
                            pricingCalcSku.set('selected', false);
                            pricingCalcSku.set('enabled', false);
                        }
                    });
                }

                // if no sku must be selected, find a new one AFTER all sku conflicts resolved
                if (!selectedSku) {
                    selectedSku =
                        previousSku ||
                        pricingCalcItem.findSkuBy({ sides: pricingCalcItem.get('sideCount') }) ||
                        pricingCalcItem.get('skus').first();
                    if (!selectedSku.get('enabled')) {
                        selectedSku = pricingCalcItem.findClosestEnabledSku(selectedSku);
                    }
                    selectedSku.set('selected', true);
                    //resolve conflicts for new selected sku
                    optionConflicts = _.union(optionConflicts, selectedSku.get('sku').get('optionValueConflicts'));
                    resolveConflictsAndDependencies();
                }

                pricingCalcItem.selectSide(selectedSku.get('sku').get('sides'));

                function isResolvedOption(optionName) {
                    return _.has(selectedOptions, optionName);
                }

                function isFullfilledDependencies(pricingCalcOptionValue) {
                    var fulfilled = true;
                    pricingCalcOptionValue
                        .get('optionValue')
                        .get('optionValueDependencies')
                        .forEach(function(requiredName) {
                            var requiredOption = pricingCalcItem.findOptionByOptionValueName(requiredName);
                            if (requiredOption) {
                                var requiredOptionValue = requiredOption.getOptionValue(requiredName);
                                var requiredOptionName = requiredOption.get('option').get('name');
                                // if dependency is disabled or option has other option value selected
                                if (
                                    !requiredOptionValue.get('enabled') ||
                                    (isResolvedOption(requiredOptionName) && selectedOptions[requiredOptionName] !== requiredName)
                                ) {
                                    fulfilled = false;
                                }
                            }
                        });

                    return fulfilled;
                }

                function selectOptionValue(pricingCalcOptionValue, optionName) {
                    var name = pricingCalcOptionValue.get('optionValue').get('name');

                    // gather dependencies and conflicts
                    optionDependencies = _.union(
                        optionDependencies,
                        pricingCalcOptionValue.get('optionValue').get('optionValueDependencies')
                    );
                    optionConflicts = _.union(optionConflicts, pricingCalcOptionValue.get('optionValue').get('optionValueConflicts'));
                    skuConflicts = _.union(skuConflicts, pricingCalcOptionValue.get('optionValue').get('skuConflicts'));
                    if (!_.contains(resolvedDependencies, name)) {
                        resolvedDependencies.push(name);
                    }

                    // select option value
                    pricingCalcOptionValue.set('selected', true);
                    selectedOptions[optionName] = name;
                }

                function resolveConflictsAndDependencies() {
                    var currentOptionDependencies = _.difference(optionDependencies, resolvedDependencies);
                    var currentOptionConflicts = _.difference(optionConflicts, resolvedConflicts);

                    if (!currentOptionDependencies.length && !currentOptionConflicts.length) {
                        return;
                    }

                    // Resolve option value conflicts and dependencies recursively
                    pricingCalcItem.get('options').each(function(pricingCalcOption) {
                        optionName = pricingCalcOption.get('option').get('name');
                        pricingCalcOption.get('optionValues').each(function(pricingCalcOptionValue) {
                            optionValueName = pricingCalcOptionValue.get('optionValue').get('name');
                            if (_.contains(currentOptionConflicts, optionValueName)) {
                                pricingCalcOptionValue.set('enabled', false);
                            }
                            if (_.contains(currentOptionDependencies, optionValueName)) {
                                selectOptionValue(pricingCalcOptionValue, optionName);
                            }
                        });
                    });

                    // we suppose all conflicts and dependencies for current iteration were resolved
                    resolvedDependencies = _.union(resolvedDependencies, currentOptionDependencies);
                    resolvedConflicts = _.union(resolvedConflicts, currentOptionConflicts);

                    resolveConflictsAndDependencies();
                }

                // Select option and resolve its conflicts and dependencies ONE BY ONE, cause it may affect next options
                function resolveOptions() {
                    var pricingCalcOption = pricingCalcItem.get('options').find(function(pricingCalcOption) {
                        optionName = pricingCalcOption.get('option').get('name');
                        return !isResolvedOption(optionName);
                    });
                    if (!pricingCalcOption) {
                        return;
                    }
                    var selectedOptionValue = null;
                    var previousOptionValueFound = false;

                    pricingCalcOption.get('optionValues').each(function(pricingCalcOptionValue) {
                        optionValueName = pricingCalcOptionValue.get('optionValue').get('name');

                        if (pricingCalcOptionValue.get('enabled') && !previousOptionValueFound) {
                            // save first enabled option value
                            if (!selectedOptionValue) {
                                if (isFullfilledDependencies(pricingCalcOptionValue)) {
                                    selectedOptionValue = pricingCalcOptionValue;
                                }
                            }

                            // if option value is default, it takes priority
                            if (pricingCalcOptionValue.get('optionValue').get('default')) {
                                if (isFullfilledDependencies(pricingCalcOptionValue)) {
                                    selectedOptionValue = pricingCalcOptionValue;
                                }
                            }
                            // if option value was selected previously, it takes priority
                            if (previousOptions[optionName] === optionValueName) {
                                if (isFullfilledDependencies(pricingCalcOptionValue)) {
                                    selectedOptionValue = pricingCalcOptionValue;
                                    previousOptionValueFound = true; //from now on there is no need to check for other option values
                                }
                            }
                        }
                    });

                    if (selectedOptionValue) {
                        selectOptionValue(selectedOptionValue, optionName);
                    } else {
                        selectedOptions[optionName] = null;
                        pricingCalcOption.set('enabled', false);
                    }

                    resolveConflictsAndDependencies();
                    resolveOptions();
                }

                pricingCalcItem.set('optionSet', selectedOptions);
                pricingCalcItem.set('quantity', selectedSku.get('sku').get('quantity'));
                pricingCalcItem.set('sideCount', selectedSku.get('sku').get('sides'));
            },

            /**
             * @param {PricingCalcItemModel} pricingCalcItem
             * @param {PricingCalcItemModel} oldPricingCalcItem
             * @returns {PricingCalcItemModel}
             */
            makeSimilar: function(pricingCalcItem, oldPricingCalcItem) {
                this.selectSkuAndOptions(
                    pricingCalcItem,
                    {},
                    null,
                    oldPricingCalcItem.get('optionSet'),
                    oldPricingCalcItem.get('quantity'),
                    oldPricingCalcItem.get('sideCount')
                );

                this.updateItemPrices(pricingCalcItem);

                return pricingCalcItem;
            },

            /**
             * @param {PricingCalcItemModel} pricingCalcItem
             */
            updateItemPrices: function(pricingCalcItem) {
                var selectedSku = pricingCalcItem.getSelectedSku();
                var pricingSetName = this.model.get('pricingSetName');
                var itemPrice = selectedSku.get('sku').getPrice();
                var itemSalePrice = selectedSku.get('sku').getPrice(pricingSetName);

                var selectedOptionValues = pricingCalcItem.getSelectedOptionValues();

                pricingCalcItem.get('options').forEach(function(pricingCalcOption) {
                    var isNonBookletSubstrate =
                        pricingCalcOption.get('option').get('name') === OptionModel.NAME_SUBSTRATE &&
                        !pricingCalcItem.get('product').isBooklet();
                    pricingCalcOption.get('optionValues').forEach(function(pricingCalcOptionValue) {
                        var optionValuePrice = pricingCalcOptionValue.getPrice(selectedSku, selectedOptionValues);
                        var optionValueSalePrice = pricingCalcOptionValue.getPrice(
                            selectedSku,
                            selectedOptionValues,
                            pricingSetName,
                            isNonBookletSubstrate
                        );
                        pricingCalcOptionValue.set('price', optionValuePrice);
                        pricingCalcOptionValue.set('salePrice', optionValueSalePrice);

                        if (pricingCalcOptionValue.get('selected')) {
                            pricingCalcOption.set('price', optionValuePrice);
                            pricingCalcOption.set('salePrice', optionValueSalePrice);
                            itemPrice += optionValuePrice;
                            itemSalePrice += optionValueSalePrice;
                        }
                    });
                });

                pricingCalcItem.set('price', itemPrice);
                pricingCalcItem.set('salePrice', itemSalePrice);
            },

            /**
             * @param {Object} shipping
             */
            changeShipping: function(shipping) {
                if (shipping !== undefined) {
                    this.model.setShipping(shipping);
                } else {
                    this.model.dropShipping();
                }
                this.model.get('eventAgent').trigger('PricingCalculator:shippingChanged', this.model.get('shipping'));
                this.updateTotalPrice();
            },

            updateTotalPrice: function() {
                var productPrice = this.model.get('pricingCalcItems').reduce(function(sum, pricingCalcItem) {
                    return sum + pricingCalcItem.get('salePrice');
                }, 0);

                var shipping = this.model.get('shipping');
                var shippingCost = shipping ? shipping.price : 0;
                var shippingPrice = !isNaN(shippingCost) ? shippingCost : 0;
                var price = productPrice + shippingPrice;
                this.model.set('price', price);
                this.model.get('eventAgent').trigger('PricingCalculator:priceChanged');
            },

            /**
             * @param {PricingCalcItemModel[]} items
             */
            setItems: function(items) {
                this.model.set('pricingCalcItems', new backbone.Collection(items));
            },

            /**
             * @param {PricingCalcItemModel} item
             */
            addItem: function(item) {
                this.model.get('pricingCalcItems').add(item);
            },

            /**
             * @param {number} zip
             */
            setZip: function(zip) {
                this.model.set('zip', zip);
            },

            /**
             * @param {number} price
             */
            setPrice: function(price) {
                this.model.set('price', price);
            },

            /**
             * @param {string} code
             */
            setDiscountCode: function(code) {
                this.model.set('discountCode', code);
            },

            /**
             * @param {string} pricingSetName
             */
            setPricingSetName: function(pricingSetName) {
                this.model.set('pricingSetName', pricingSetName);
            },

            /**
             * @param {Object} obj
             */
            setAvailableShipping: function(obj) {
                this.model.set('availableShipping', obj);
            },

            /**
             * @param {number|null} price
             */
            setShippingPrice: function(price) {
                this.model.setShipping({ price: price });
            }
        };

        return PricingCalculator;
    }
);
